'use client';

import api from '@/axiosSetup';
import React, { createContext, useState, useContext, useCallback, useRef } from 'react';

// Create a Context
const BookingsBoxContext = createContext();

// Create a Provider Component
function BookingsBoxProvider({children}) {
  const [periodUsers, setPeriodUsers] = useState([]);
  const [periodBookedDates, setPeriodBookedDates] = useState([]);
  const [periodBookedSpecialClientDates, setPeriodBookedSpecialClientDates] = useState([]);
  const [periodVIPClients, setPeriodVIPClients] = useState([]);
  const [periodBookedHotelDates, setPeriodBookedHotelDates] = useState([]);
  const [periodBookedCasinoDates, setPeriodBookedCasinoDates] = useState([]);
  const [periodBookedEventsDates, setPeriodBookedEventsDates] = useState([]);
  const [periodBookedGRATDates, setPeriodBookedGRATDates] = useState([]);
  const [periodBookedCasinoOwnerDates, setPeriodBookedCasinoOwnerDates] = useState([]);
  const [periodDays, setPeriodDays] = useState([]);
  const [periodSummaryStatus, setPeriodSummaryStatus] = useState('idle');
  const [periodSummaryKey, setPeriodSummaryKey] = useState(null);
  const [periodSummaryDataKey, setPeriodSummaryDataKey] = useState(null);
  const latestRequest = useRef(0);

  const getPeriodDays = useCallback(async (currentMonth, currentYear) => {
    if (!Number.isInteger(currentMonth) || !Number.isInteger(currentYear)) return;
    const request = ++latestRequest.current;
    setPeriodSummaryKey(`${currentMonth}-${currentYear}`);
    setPeriodSummaryStatus('loading');
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Access-Control-Allow-Origin': '*', 
          "Cache-Control": "no-cache",
        },
      };
      const response = await api.get(`/api/period-view-booked-users?firstMonth=${currentMonth}&year=${currentYear}`, config);
      // Ignore a slow response for a period the user has already navigated away from.
      if (request !== latestRequest.current) return;
      const data = response.data;
      if (!['bookedUsersMCodes', 'allMCodes', 'allSpecialClientIds', 'days'].every(key => Array.isArray(data[key]))) {
        throw new Error('Invalid period booking count.');
      }

      // Prepare the whole response before replacing any displayed totals. A
      // failed refresh keeps the last successful period's values intact.
      const uniqueBookedUsersMCodes = data.bookedUsersMCodes.filter((item, index, array) => {
        const mCodes = array.map(user => user.mCode);
        return mCodes.indexOf(item.mCode) === index;
      });
      const allHotels = data.allSpecialClientIds.filter((id) => id.clientType === 'hotel');
      const allCasinos = data.allSpecialClientIds.filter((id) => id.clientType === 'casino');
      const allEvents = data.allSpecialClientIds.filter((id) => id.clientType === 'events');
      const allGRAT = data.allSpecialClientIds.filter((id) => id.clientType === 'grat');
      const allCasinoOwners = data.allSpecialClientIds.filter((id) => id.clientType === 'casinoowner');

      setPeriodUsers(data.bookedUsersMCodes);
      setPeriodBookedDates(data.allMCodes);
      setPeriodBookedSpecialClientDates(data.allSpecialClientIds);
      setPeriodVIPClients(uniqueBookedUsersMCodes);
      setPeriodBookedHotelDates(allHotels);
      setPeriodBookedCasinoDates(allCasinos);
      setPeriodBookedEventsDates(allEvents);
      setPeriodBookedGRATDates(allGRAT);
      setPeriodBookedCasinoOwnerDates(allCasinoOwners);
      setPeriodDays(data.days);
      setPeriodSummaryDataKey(`${currentMonth}-${currentYear}`);
      setPeriodSummaryStatus('ready');
    } catch (error) {
      if (request !== latestRequest.current) return;
      setPeriodSummaryStatus('error');
    }
  }, []);

  return (
    <BookingsBoxContext.Provider value={{
      periodUsers,
      setPeriodUsers,
      periodBookedDates,
      setPeriodBookedDates,
      periodBookedSpecialClientDates,
      setPeriodBookedSpecialClientDates,
      periodVIPClients,
      setPeriodVIPClients,
      periodBookedHotelDates,
      setPeriodBookedHotelDates,
      periodBookedCasinoDates,
      setPeriodBookedCasinoDates,
      periodBookedEventsDates,
      setPeriodBookedEventsDates,
      periodBookedGRATDates,
      setPeriodBookedGRATDates,
      periodBookedCasinoOwnerDates,
      setPeriodBookedCasinoOwnerDates,
      periodDays,
      setPeriodDays,
      periodSummaryStatus,
      periodSummaryKey,
      periodSummaryDataKey,
      getPeriodDays,
    }}>
      {children}
    </BookingsBoxContext.Provider>
  );

}

// Custom hook to use the BookingsBoxContext
const useBookingsBoxContext = () => {
  const context = useContext(BookingsBoxContext);
  if (!context) throw new Error('Issue with provider');
  return context;
};

export { BookingsBoxProvider, useBookingsBoxContext };
